import React, { useState, useEffect } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';

// Style object for TextFields and Select (overriding focus color)
const inputStyle = {
  '& label.Mui-focused': { color: 'var(--color-brand)' },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': { borderColor: 'var(--color-brand)' }
  },
  '& .MuiInputLabel-root': { color: '#666' },
  '& .MuiSelect-select': { color: '#333' }
};

// List of continental U.S. timezones
const usTimezones = [
  { value: 'America/New_York', label: 'Eastern Time (America/New_York)' },
  { value: 'America/Chicago', label: 'Central Time (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain Time (America/Denver)' },
  { value: 'America/Phoenix', label: 'Mountain Standard Time (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (America/Los_Angeles)' }
];

// Define admin users (same as in SiteContacts.js and App.js)
const ADMIN_USERS = [
  'adam.hatch@ivanhoeelectric.com',
  'angelica.folk@ivanhoeelectric.com',
  'nate@tipple.consulting'
];

const PROJECT_TEXT_COLOR = 'var(--ops-brand-deep)';
const SELECTED_SITE_INDICATOR_COLOR = 'var(--ops-brand)';
const DEFAULT_FIRE_ALERT_DISTANCE_MILES = 15;

const SiteSelector = ({ value, onChange, supabase, sites: propSites, onManageAlerts, user, layout = 'top' }) => {
  const [supabaseSites, setSupabaseSites] = useState([]);
  const [siteActiveStatus, setSiteActiveStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openEditSites, setOpenEditSites] = useState(false);
  const [currentSite, setCurrentSite] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  // State for deletion confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);

  // Check if current user is admin
  const isAdmin = user?.email && ADMIN_USERS.includes(user.email.toLowerCase());

  // Normalize site input so tabs can be styled from alliance metadata.
  const displaySites = (propSites || []).map((site) =>
    typeof site === 'string' ? { site_name: site, BHP_Alliance: false } : site
  );

  const isBhpAllianceSite = (site) => Boolean(site?.BHP_Alliance ?? site?.bhp_alliance);
  const hasBhpAllianceSites = displaySites.some((site) => isBhpAllianceSite(site));
  const isRail = layout === 'rail';

  // Fetch sites directly from Supabase and sort alphabetically
  const fetchSites = async () => {
    setLoading(true);
    setError(null);
    try {
      const [sitesResult, thresholdsResult] = await Promise.all([
        supabase.from('sites').select('site_name, latitude, longitude, timezone'),
        supabase.from('alert_thresholds').select('site_name, is_active'),
      ]);
      if (sitesResult.error) throw sitesResult.error;
      const sortedSites = (sitesResult.data || []).sort((a, b) => a.site_name.localeCompare(b.site_name));
      setSupabaseSites(sortedSites);
      const statusMap = {};
      (thresholdsResult.data || []).forEach(t => { statusMap[t.site_name] = t.is_active; });
      setSiteActiveStatus(statusMap);
    } catch (err) {
      console.error('Error fetching sites from Supabase:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, []);

  // Open "Edit Sites" dialog
  const handleOpenEditSites = () => {
    setIsEditing(false);
    setCurrentSite({ site_name: '', latitude: '', longitude: '', timezone: '' });
    setOpenEditSites(true);
  };


  // Open "Edit Site" dialog
  const handleOpenEditSite = (site) => {
    setIsEditing(true);
    setCurrentSite({ ...site });
    setOpenEditSites(true);
  };

  // Close the Add/Edit dialog
  const handleCloseEditSites = () => {
    setOpenEditSites(false);
    setCurrentSite(null);
  };

  // Save site (add or update) using Supabase
  const handleSaveSite = async () => {
    try {
      if (isEditing) {
        const { error } = await supabase
          .from('sites')
          .update({
            latitude: currentSite.latitude,
            longitude: currentSite.longitude,
            timezone: currentSite.timezone
          })
          .eq('site_name', currentSite.site_name);
        if (error) throw error;
      } else {
        // Check limit when adding new site (counts active sites only)
        const activeSiteCount = supabaseSites.filter(s => siteActiveStatus[s.site_name] !== false).length;
        if (activeSiteCount >= 9) {
          alert('Maximum of 9 active sites allowed to prevent data overage charges. Please deactivate or delete a site before adding a new one, or contact Tipple Consulting if you need to add more sites.');
          return;
        }
        const { error } = await supabase
          .from('sites')
          .insert([currentSite]);
        if (error) throw error;

        const { error: thresholdError } = await supabase
          .from('alert_thresholds')
          .upsert({
            site_name: currentSite.site_name,
            is_active: true,
            wind_metric: 'gust',
            wind_threshold_kph: 25 * 1.60934,
            pm25_threshold_category: 'Unhealthy',
            pm10_threshold_category: 'Unhealthy',
            cooldown_minutes: 120,
            active_time_start: '00:00:00',
            active_time_end: '23:59:59',
            wildfire_enabled: true,
            fire_alert_distance_miles: DEFAULT_FIRE_ALERT_DISTANCE_MILES,
            fire_exclude_rx_burns: true,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'site_name'
          });
        if (thresholdError) throw thresholdError;
      }
      await fetchSites();
      handleCloseEditSites();
    } catch (err) {
      console.error('Error saving site to Supabase:', err);
      alert(`Error: ${err.message}`);
    }
  };

  // Open deletion confirmation dialog for a given site
  const handleRequestDeleteSite = (site) => {
    setSiteToDelete(site);
    setOpenDeleteDialog(true);
  };

  // Confirm deletion of a site using Supabase
  const confirmDeleteSite = async () => {
    if (!siteToDelete) return;
    try {
      const { error } = await supabase
        .from('sites')
        .delete()
        .eq('site_name', siteToDelete.site_name);
      if (error) throw error;
      await fetchSites();
    } catch (err) {
      console.error('Error deleting site from Supabase:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setOpenDeleteDialog(false);
      setSiteToDelete(null);
    }
  };

  if (loading) {
    return <CircularProgress sx={{ color: 'var(--color-brand)' }} />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }


  const activeSiteCount = supabaseSites.filter(s => siteActiveStatus[s.site_name] !== false).length;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'stretch',
        flexDirection: 'column',
        gap: 1.1,
        px: 1.1,
        py: 1.1,
        borderRadius: '18px',
        border: '1px solid var(--ops-border)',
        background:
          'linear-gradient(145deg, rgba(255, 255, 255, 0.97), rgba(245, 250, 248, 0.95) 50%, rgba(255, 255, 255, 0.95))',
        boxShadow: '0 14px 30px rgba(13, 40, 34, 0.1)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Box sx={{ px: 0.4, pt: 0.2 }}>
        <Typography variant="overline" sx={{ letterSpacing: 1.3, fontWeight: 800, color: 'rgba(11, 79, 74, 0.78)' }}>
          Project Selector
        </Typography>
      </Box>
      {/* Left Side: Site Tabs */}
      <Tabs
        value={value}
        onChange={onChange}
        variant={isRail ? 'standard' : 'scrollable'}
        orientation={isRail ? 'vertical' : 'horizontal'}
        scrollButtons={isRail ? false : 'auto'}
        sx={{
          width: '100%',
          minHeight: isRail ? 0 : 48,
          maxHeight: 'none',
          '& .MuiTabs-scroller': {
            overflowY: 'visible !important',
            overflowX: isRail ? 'visible !important' : 'auto',
          },
          '& .MuiTabs-flexContainer': { gap: 0.45 },
          '& .MuiTabs-indicator': {
            backgroundColor: SELECTED_SITE_INDICATOR_COLOR,
            width: isRail ? 3 : undefined,
            borderRadius: 10,
          },
          '& .MuiTab-root': {
            minHeight: isRail ? 38 : 44,
            minWidth: isRail ? 0 : 120,
            borderRadius: '10px',
            transition: 'all 180ms ease',
            justifyContent: 'center',
            textAlign: 'left',
            alignItems: 'flex-start',
            px: 1.1,
            py: 0.8,
            lineHeight: 1.2,
          },
          '& .MuiTab-root:hover': {
            backgroundColor: 'rgba(11, 79, 74, 0.08)',
          },
        }}
      >
        {displaySites.map((site) => (
          <Tab
            key={site.site_name}
            label={`${site.site_name}${isBhpAllianceSite(site) ? ' *' : ''}`}
            value={site.site_name}
            sx={{
              color: PROJECT_TEXT_COLOR,
              fontWeight: 600,
              letterSpacing: 0.2,
              fontSize: '0.84rem',
              '&.Mui-selected': {
                color: PROJECT_TEXT_COLOR,
                backgroundColor: site.site_name === value ? 'rgba(255, 255, 255, 0.9)' : 'transparent',
                boxShadow: site.site_name === value ? '0 4px 14px rgba(14, 52, 43, 0.09)' : 'none',
              },
            }}
          />
        ))}
      </Tabs>

      {/* Actions: Manage Alerts and Edit Sites */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isRail ? '1fr' : { xs: '1fr', sm: isAdmin ? '1fr 1fr' : '1fr' },
          alignItems: 'center',
          gap: 0.9,
          px: 0.25,
        }}
      >
        <Button
          variant="contained"
          onClick={onManageAlerts}
          sx={{
            backgroundColor: 'var(--ops-brand)',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'var(--ops-brand-deep)',
              boxShadow: 'none',
            },
          }}
        >
          Manage Alerts
        </Button>
        {isAdmin && (
          <Button
          variant="outlined"
          onClick={handleOpenEditSites}
          sx={{
              color: 'var(--ops-brand)',
              borderColor: 'rgba(15, 118, 110, 0.45)',
              '&:hover': {
                borderColor: 'var(--ops-brand)',
                backgroundColor: 'rgba(15, 118, 110, 0.08)',
              },
            }}
          >
            Edit Sites
          </Button>
        )}
      </Box>
      {hasBhpAllianceSites && (
        <Typography
          variant="caption"
          sx={{
            px: 0.45,
            color: 'var(--ops-muted)',
            fontStyle: 'italic',
            letterSpacing: 0.1,
          }}
        >
          * BHP Alliance Site
        </Typography>
      )}

      {/* Edit Sites Dialog */}
      <Dialog
        open={openEditSites}
        onClose={handleCloseEditSites}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(120deg, var(--color-brand-dark), var(--color-brand))',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.25,
          }}
        >
          <Typography variant="h6">
            {isEditing ? 'Edit Site' : 'Add or Edit Sites'}
          </Typography>
          <IconButton onClick={handleCloseEditSites} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent>
          {/* Display existing sites table if not editing a single site */}
          {!isEditing && (
            <TableContainer
              component={Paper}
              sx={{
                border: '1px solid var(--color-border)',
                boxShadow: 'none',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Site Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Latitude</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Longitude</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Timezone</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {supabaseSites.map((site) => (
                    <TableRow key={site.site_name}>
                      <TableCell>{site.site_name}</TableCell>
                      <TableCell>{site.latitude}</TableCell>
                      <TableCell>{site.longitude}</TableCell>
                      <TableCell>{site.timezone}</TableCell>
                      <TableCell>
                        {siteActiveStatus[site.site_name] === false ? (
                          <Chip label="Inactive" size="small" sx={{ bgcolor: '#f8d7da', color: '#842029', fontWeight: 600 }} />
                        ) : (
                          <Chip label="Active" size="small" sx={{ bgcolor: '#d1e7dd', color: '#0a3622', fontWeight: 600 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleOpenEditSite(site)}
                          sx={{ color: 'var(--color-brand)' }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleRequestDeleteSite(site)}
                          sx={{ color: '#e74c3c' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Show limit message when not editing and active site limit reached */}
          {!isEditing && activeSiteCount >= 9 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#fff7ea', border: '1px solid rgba(227, 83, 2, 0.3)', borderRadius: 1.5 }}>
              <Typography variant="body2" sx={{ color: '#856404' }}>
                Maximum of 9 active sites allowed to prevent data overage charges. Please deactivate or delete a site before adding a new one, or contact Tipple Consulting if you need to add more sites.
              </Typography>
            </Box>
          )}

          {/* Add/Edit Site Form on one row with icon button at the far right */}
          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr auto' },
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <TextField
              label="Site Name"
              value={currentSite?.site_name || ''}
              onChange={(e) =>
                setCurrentSite({ ...currentSite, site_name: e.target.value })
              }
              fullWidth
              disabled={isEditing} // Prevent editing site_name for existing sites
              sx={inputStyle}
            />
            <TextField
              label="Latitude"
              value={currentSite?.latitude || ''}
              onChange={(e) =>
                setCurrentSite({ ...currentSite, latitude: e.target.value })
              }
              fullWidth
              type="number"
              sx={inputStyle}
            />
            <TextField
              label="Longitude"
              value={currentSite?.longitude || ''}
              onChange={(e) =>
                setCurrentSite({ ...currentSite, longitude: e.target.value })
              }
              fullWidth
              type="number"
              sx={inputStyle}
            />
            <FormControl fullWidth sx={inputStyle}>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={currentSite?.timezone || ''}
                onChange={(e) =>
                  setCurrentSite({ ...currentSite, timezone: e.target.value })
                }
                label="Timezone"
              >
                {usTimezones.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              onClick={handleSaveSite}
              disabled={!isEditing && activeSiteCount >= 9}
              sx={{
                color: (!isEditing && activeSiteCount >= 9) ? '#ccc' : '#2ecc71',
                ml: 'auto',
                backgroundColor: 'rgba(46, 204, 113, 0.12)',
                '&:hover': {
                  backgroundColor: 'rgba(46, 204, 113, 0.22)',
                },
              }}
            >
              {isEditing ? <SaveIcon /> : <AddIcon />}
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{siteToDelete?.site_name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: 'var(--color-brand)' }}>
            No
          </Button>
          <Button onClick={confirmDeleteSite} variant="contained" color="error">
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SiteSelector;
